import type { PublicSystemHealth } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

export interface UsePublicSystemHealthReturn {
	health: PublicSystemHealth | null;
	isLoading: boolean;
	error: Error | null;
}

const POLL_INTERVAL_MS = 30_000;

/**
 * Polls the system health endpoint every 30 seconds and returns the parsed
 * `PublicSystemHealth` object. Includes the 30-day event volume buckets
 * introduced in Phase 7.
 */
export function usePublicSystemHealth(): UsePublicSystemHealthReturn {
	const client = useApiClient();

	const query = useQuery({
		queryKey: queryKeys.publicVerification.systemHealth,
		queryFn: async () => {
			const result = await client.publicVerification.getSystemHealth();
			if (result.status === 200) {
				return result.body;
			}
			throw new Error("Failed to fetch system health");
		},
		refetchInterval: POLL_INTERVAL_MS,
	});

	return {
		health: query.data ?? null,
		isLoading: query.isLoading,
		error: query.error,
	};
}
